import { Helmet } from 'react-helmet-async';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card, Table, Stack, Chip, Button, TableRow, TableBody, TableCell, TableHead, Container, Typography,
  TableContainer, TablePagination, TextField, InputAdornment, ToggleButton, ToggleButtonGroup, Grid, Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, getFirestore, orderBy, query, doc, getDoc } from 'firebase/firestore';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import { TableSkeleton } from '../components/loading';
import firebaseApp from '../utils/firebaseConfig';
import { matchesSearch } from '../utils/search';
import { formatPhoneDisplay } from '../utils/phone';
import {
  DEFAULT_LAYAWAY_SETTINGS, daysLeft, effectiveStatus, firearmLabel, fmtDate, formatPrice, isCustody, firearmCount, monthlyStorageCents, statusMeta,
} from '../utils/layaways';
import LayawayCreateDialog from '../sections/@dashboard/layaways/LayawayCreateDialog';
import LayawaySettingsDialog from '../sections/@dashboard/layaways/LayawaySettingsDialog';

const db = getFirestore(firebaseApp);

// Layaways de armas (pedido 2026-10-05). El staff entra el layaway cuando el
// cliente firma el contrato; el cliente abona desde la app/web. Aquí: lista
// con filtros por estado, totales y acceso a la ficha.
const FILTERS = [
  { key: 'open', label: 'Abiertos' },
  { key: 'custody', label: 'Custodias' },
  { key: 'overdue', label: 'Vencidos' },
  { key: 'paid', label: 'Saldados (por entregar)' },
  { key: 'completed', label: 'Entregados' },
  { key: 'cancelled', label: 'Cancelados' },
  { key: 'all', label: 'Todos' },
];

export default function LayawaysPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [createOpen, setCreateOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_LAYAWAY_SETTINGS);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const [snap, s] = await Promise.all([
        getDocs(query(collection(db, 'layaways'), orderBy('createdAt', 'desc'))),
        getDoc(doc(db, 'settings', 'layaways')),
      ]);
      setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setSettings({ ...DEFAULT_LAYAWAY_SETTINGS, ...(s.exists() ? s.data() : {}) });
    } catch (e) {
      console.error('layaways', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const filtered = useMemo(() => rows.filter((l) => {
    const st = effectiveStatus(l);
    // "Abiertos" = activos y vencidos (incluye el saldado vencido que debe almacenaje)
    if (filter === 'open' && !(st === 'active' || st === 'overdue')) return false;
    if (filter === 'custody' && !(isCustody(l) && ['active', 'overdue'].includes(st))) return false;
    if (!['open', 'custody', 'all'].includes(filter) && st !== filter) return false;
    if (!search.trim()) return true;
    return matchesSearch(search, [l.memberName, l.memberPhone, l.memberEmail, l.brand, l.model, l.serialNumber, l.caliber, l.contractNumber, ...(l.firearms || []).flatMap((f) => [f.brand, f.model, f.serialNumber])]);
  }), [rows, filter, search]);

  const totals = useMemo(() => {
    const open = rows.filter((l) => ['active', 'overdue'].includes(effectiveStatus(l)));
    return {
      open: open.length,
      overdue: rows.filter((l) => effectiveStatus(l) === 'overdue').length,
      paid: rows.filter((l) => effectiveStatus(l) === 'paid').length,
      custody: open.filter(isCustody).length,
      balance: open.reduce((s, l) => s + (Number(l.balanceCents) || 0), 0),
    };
  }, [rows]);

  const pageRows = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <Helmet><title> Layaways | BBTC </title></Helmet>
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={1}>
          <div>
            <Typography variant="h4">Layaways de armas</Typography>
            <Typography variant="body2" color="text.secondary">El cliente abona desde la app (Perfil → Mis Layaways) o la web /layaways. Aquí se crean y se registran los pagos en tienda.</Typography>
          </div>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<Iconify icon="eva:credit-card-outline" />} onClick={() => navigate('/dashboard/layaways/payments')}>Pagos</Button>
            <Button variant="outlined" startIcon={<Iconify icon="eva:settings-2-outline" />} onClick={() => setSettingsOpen(true)}>Tarifas</Button>
            <Button variant="outlined" startIcon={<Iconify icon="eva:refresh-fill" />} onClick={fetchRows}>Actualizar</Button>
            <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => setCreateOpen(true)}>Nuevo layaway / custodia</Button>
          </Stack>
        </Stack>

        <Grid container spacing={2} mb={3}>
          {[
            { label: 'Abiertos', value: `${totals.open}${totals.custody ? ` (${totals.custody} custodia${totals.custody === 1 ? '' : 's'})` : ''}` },
            { label: 'Vencidos', value: totals.overdue, color: totals.overdue ? 'error.main' : undefined },
            { label: 'Saldados por entregar', value: totals.paid, color: totals.paid ? 'success.main' : undefined },
            { label: 'Balance por cobrar', value: formatPrice(totals.balance) },
          ].map((k) => (
            <Grid item xs={6} md={3} key={k.label}>
              <Card sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">{k.label}</Typography>
                <Typography variant="h4" color={k.color}>{k.value}</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ p: 2 }} alignItems={{ md: 'center' }}>
            <ToggleButtonGroup exclusive size="small" value={filter} onChange={(e, v) => { if (v) { setFilter(v); setPage(0); } }}>
              {FILTERS.map((f) => <ToggleButton key={f.key} value={f.key}>{f.label}</ToggleButton>)}
            </ToggleButtonGroup>
            <TextField
              size="small" placeholder="Buscar por nombre, teléfono, marca, serie…" value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }} sx={{ minWidth: 300 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} /></InputAdornment> }}
            />
          </Stack>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 900 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Arma</TableCell>
                    <TableCell>Serie</TableCell>
                    <TableCell align="right">Precio</TableCell>
                    <TableCell align="right">Pagado</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell>Fecha límite</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && <TableSkeleton rows={6} columns={9} />}
                  {!loading && pageRows.length === 0 && (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 6, color: 'text.secondary' }}>No hay layaways en este filtro.</TableCell></TableRow>
                  )}
                  {!loading && pageRows.map((l) => {
                    const st = effectiveStatus(l);
                    const meta = statusMeta(l, st);
                    const dl = daysLeft(l);
                    const cust = isCustody(l);
                    return (
                      <TableRow hover key={l.id} sx={{ cursor: 'pointer' }} onClick={() => navigate(`/dashboard/layaways/details?id=${l.id}`)}>
                        <TableCell>
                          <Typography variant="subtitle2">{l.memberName || '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">{formatPhoneDisplay(l.memberPhone)}{l.memberId ? '' : ' · sin cuenta'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{firearmLabel(l)}</Typography>
                          <Typography variant="caption" color="text.secondary">{cust ? `${firearmCount(l)} arma(s) · ${formatPrice(monthlyStorageCents(l, settings))}/mes` : `${l.firearmType}${l.condition === 'used' ? ' · usada' : ' · nueva'}`}</Typography>
                        </TableCell>
                        <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{cust ? (l.firearms || []).map((f) => f.serialNumber).filter(Boolean).join(', ') || '—' : (l.serialNumber || '—')}</Typography></TableCell>
                        <TableCell align="right">{cust ? <Chip size="small" variant="outlined" label="Custodia" /> : formatPrice(l.priceCents)}</TableCell>
                        <TableCell align="right">{cust ? `${l.storageMonths || 0} mes(es)` : formatPrice(l.paidCents)}</TableCell>
                        <TableCell align="right">{cust ? <Typography variant="caption" color="text.secondary">almacenaje</Typography> : <Typography variant="subtitle2" color={l.balanceCents > 0 ? 'error.main' : 'success.main'}>{formatPrice(l.balanceCents)}</Typography>}</TableCell>
                        <TableCell>
                          {fmtDate(l.dueDate)}
                          {['active', 'overdue'].includes(st) && dl !== null && (
                            <Typography variant="caption" display="block" color={dl < 0 ? 'error.main' : dl <= 7 ? 'warning.main' : 'text.secondary'}>
                              {dl < 0 ? `venció hace ${-dl} d${l.status === 'paid' ? ' · debe almacenaje' : ''}` : dl === 0 ? 'vence hoy' : `faltan ${dl} d`}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" color={meta.color} label={meta.label} />
                          {l.storageMonths > 0 && <Tooltip title={`${l.storageMonths} mes(es) de almacenaje pagados`}><Chip size="small" variant="outlined" sx={{ ml: 0.5 }} label={`+${l.storageMonths}m`} /></Tooltip>}
                        </TableCell>
                        <TableCell align="right"><Iconify icon="eva:arrow-ios-forward-fill" /></TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
          <TablePagination
            component="div" count={filtered.length} page={page} rowsPerPage={rowsPerPage}
            onPageChange={(e, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]} labelRowsPerPage="Por página"
          />
        </Card>
      </Container>

      <LayawayCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(id) => { fetchRows(); navigate(`/dashboard/layaways/details?id=${id}`); }} />
      <LayawaySettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} settings={settings} onSaved={(s) => setSettings({ ...settings, ...s })} />
    </>
  );
}
